const AUTH_PATHS = ['/login', '/sign-up', '/logout'];

/**
 * Accepts same-origin absolute URLs (strips the origin) and plain relative paths.
 * Rejects everything else to prevent open-redirect when the value comes from
 * an untrusted source like localStorage.
 * Also rejects auth-flow paths (/login, /sign-up) since redirecting
 * back to those after authentication creates an infinite loop.
 */
export const sanitizeRedirectUrl = (
  url: string | undefined,
  currentOrigin = window.location.origin,
): string | undefined => {
  if (!url) return undefined;

  if (url.startsWith('/') && !url.startsWith('//')) {
    // Browsers read "/\evil.com" (and "/<tab>/evil.com") as "//evil.com", another site, so resolve the
    // path the way the browser will and keep it only if it stays on this origin.
    let resolved: URL;
    try {
      resolved = new URL(url, currentOrigin);
    } catch {
      return undefined;
    }
    if (resolved.origin !== currentOrigin || /[\\\t\n\r]/.test(url)) return undefined;
    const path = resolved.pathname;
    if (path === '/') return undefined;
    if (AUTH_PATHS.some((p) => path === p || path.startsWith(p + '/'))) return undefined;
    return path + resolved.search + resolved.hash;
  }

  try {
    const parsed = new URL(url);
    if (parsed.origin === currentOrigin) {
      const path = parsed.pathname;
      if (AUTH_PATHS.some((p) => path === p || path.startsWith(p + '/'))) return undefined;
      return path + parsed.search + parsed.hash;
    }
  } catch {
    // not a parseable URL — fall through to reject
  }

  return undefined;
};

/**
 * Remembers where to send the user once they've signed in, for an hour. Login reads it back.
 * Ignores anything sanitizeRedirectUrl rejects. Returns what it stored.
 */
export const rememberRedirectUrl = (url: string | undefined): string | undefined => {
  const validRedirect = sanitizeRedirectUrl(url);
  if (validRedirect) {
    localStorage.setItem('redirectUrl', validRedirect);
    const expiration = new Date();
    expiration.setHours(expiration.getHours() + 1);
    localStorage.setItem('redirectUrlExpiration', expiration.toISOString());
  }
  return validRedirect;
};
